import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Crop, RotateCcw, RefreshCw, Loader2, Move } from "lucide-react";
import { Slider } from "../ui/slider";
import { CropDialog } from "./CropDialog";
import { uploadImage, useFileData } from "../../lib/images";

const Btn = ({ children, ...p }) => (
  <button type="button" {...p} className="flex items-center gap-1.5 border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:border-slate-900 disabled:opacity-50">{children}</button>
);

export const UploadButton = ({ label, testId, onFile, busy, primary }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testId}-input`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}
        className={primary ? "btn-blaze flex items-center gap-2 px-4 py-2.5 text-sm" : "flex items-center gap-1.5 border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide hover:border-slate-900"}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : primary ? <Upload className="h-4 w-4" /> : <RefreshCw className="h-3.5 w-3.5" />} {label}
      </button>
    </>
  );
};

export const useUploader = (onDone) => {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    setBusy(true);
    try { onDone(await uploadImage(f)); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return { busy, upload };
};

// placement: {file, crop, zoom, x, y}
export const ImageControls = ({ placement, onChange, label, aspect, minZoom = 0.5, testPrefix }) => {
  const [cropOpen, setCropOpen] = useState(false);
  const src = useFileData(placement.file);
  const { busy, upload } = useUploader((file) => onChange({ file, crop: null, zoom: 1, x: 0, y: 0 }));
  const has = !!placement.file;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {has && src && <img src={src} alt="" className="h-14 w-14 border-2 border-slate-900 bg-[repeating-conic-gradient(#e2e8f0_0_25%,#fff_0_50%)] bg-[length:12px_12px] object-contain" data-testid={`${testPrefix}-thumb`} />}
        <UploadButton label={has ? "Replace" : `Upload ${label}`} testId={`${testPrefix}-upload-button`} onFile={upload} busy={busy} primary={!has} />
        {has && <Btn onClick={() => setCropOpen(true)} disabled={!src} data-testid={`${testPrefix}-crop-button`}><Crop className="h-3.5 w-3.5" /> Crop</Btn>}
        {has && <Btn onClick={() => onChange({ crop: null, zoom: 1, x: 0, y: 0 })} data-testid={`${testPrefix}-reset-button`}><RotateCcw className="h-3.5 w-3.5" /> Reset</Btn>}
      </div>
      {has && (
        <>
          <div>
            <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-wider text-slate-600">
              <span>Zoom</span><span className="font-mono-cc" data-testid={`${testPrefix}-zoom-value`}>{placement.zoom.toFixed(2)}×</span>
            </div>
            <Slider min={minZoom} max={4} step={0.01} value={[placement.zoom]} onValueChange={([z]) => onChange({ zoom: z })} data-testid={`${testPrefix}-zoom-slider`} />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><Move className="h-3.5 w-3.5" /> Drag the image on the shield preview to reposition it.</p>
        </>
      )}
      <p className="text-xs text-slate-500">JPG, PNG or WebP, up to 15 MB.</p>
      <CropDialog target={cropOpen ? { src, aspect, crop: placement.crop, label } : null} onClose={() => setCropOpen(false)}
        onSave={(crop) => { onChange({ crop, zoom: 1, x: 0, y: 0 }); setCropOpen(false); }} />
    </div>
  );
};
